import type {
  OptimizationAgentFeedback,
  OptimizationBlockState,
  ResumeOptimizationRun,
  SkillPreMatch,
} from './types'

export type { SkillPreMatch }

export interface SkillGapSummary {
  preMatch: SkillPreMatch
  recommendations: string[]
  greenCount: number
  sectionGroups: SkillGapSectionGroup[]
  validated: boolean
  fallbackMessage?: string
}

export interface SkillGapSectionGroup {
  sectionLabel: string
  subsectionLabel: string
  hint: string
  items: string[]
  tone: 'green' | 'red' | 'yellow'
}

type ServerSectionGroup = {
  section_label?: string
  subsection_label?: string
  hint?: string
  items?: string[]
  tone?: string
}

type ValidatedSkillGapPayload = {
  validated?: boolean
  fallback_message?: string
  green?: string[]
  yellow?: string[]
  red?: string[]
  green_count?: number
  pre_match?: SkillPreMatch
  section_groups?: ServerSectionGroup[]
  recommendations?: string[]
}

const MAX_SKILL_KEYWORD_CHARS = 50
const MAX_SKILL_KEYWORD_WORDS = 5
const MARKETING_SKILL_STARTERS = new Set([
  'join',
  'elevate',
  'fueling',
  'benefit',
  'tackle',
  'unleash',
  'create',
  'long-lasting',
  'cutting-edge',
  'partner',
  'individual',
])

export function isPlausibleSkillKeyword(token: string): boolean {
  const t = token.trim().toLowerCase().replace(/\s+/g, ' ')
  if (t.length < 2 || t.length > MAX_SKILL_KEYWORD_CHARS) return false
  const words = t.split(' ')
  if (words.length > MAX_SKILL_KEYWORD_WORDS) return false
  if (t.startsWith('and ')) return false
  if (t.endsWith('.')) return false
  if (words[0] && MARKETING_SKILL_STARTERS.has(words[0])) return false
  return true
}

function filterPreMatch(preMatch: SkillPreMatch): SkillPreMatch {
  return {
    green: preMatch.green.filter(isPlausibleSkillKeyword),
    yellow: preMatch.yellow.filter(isPlausibleSkillKeyword),
    red: preMatch.red.filter(isPlausibleSkillKeyword),
  }
}

function mapServerSectionGroups(groups: ServerSectionGroup[] | undefined): SkillGapSectionGroup[] {
  if (!Array.isArray(groups)) return []
  return groups
    .map((g) => ({
      sectionLabel: String(g.section_label || ''),
      subsectionLabel: String(g.subsection_label || ''),
      hint: String(g.hint || ''),
      items: Array.isArray(g.items) ? g.items.filter((x) => typeof x === 'string') : [],
      tone: (g.tone === 'green' || g.tone === 'yellow' || g.tone === 'red' ? g.tone : 'red') as
        | 'green'
        | 'red'
        | 'yellow',
    }))
    .filter((g) => g.sectionLabel && g.items.length > 0)
}

export function groupSkillGapByResumeSection(preMatch: SkillPreMatch): SkillGapSectionGroup[] {
  const filtered = filterPreMatch(preMatch)
  const groups: SkillGapSectionGroup[] = []
  if (filtered.green.length > 0) {
    groups.push({
      sectionLabel: 'Skills',
      subsectionLabel: 'Already matched',
      hint: 'These skills are on your resume and align with the job.',
      items: filtered.green,
      tone: 'green',
    })
  }
  if (filtered.red.length > 0) {
    groups.push({
      sectionLabel: 'Skills',
      subsectionLabel: 'Add manually if you have the experience',
      hint: 'The job mentions these; they are not on your skills list. Add only what you can support.',
      items: filtered.red,
      tone: 'red',
    })
  }
  if (filtered.yellow.length > 0) {
    groups.push({
      sectionLabel: 'Professional summary & work experience',
      subsectionLabel: 'Consider highlighting',
      hint: 'Already on your resume — weave into summary or bullets if relevant. We will not edit these for you.',
      items: filtered.yellow,
      tone: 'yellow',
    })
  }
  return groups
}

function normalizePreMatch(raw: unknown): SkillPreMatch | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const pick = (key: string) =>
    Array.isArray(row[key])
      ? row[key].filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
      : []
  const green = pick('green').filter(isPlausibleSkillKeyword)
  const yellow = pick('yellow').filter(isPlausibleSkillKeyword)
  const red = pick('red').filter(isPlausibleSkillKeyword)
  if (!green.length && !yellow.length && !red.length) return null
  return { green, yellow, red }
}

function summaryFromValidatedPayload(payload: ValidatedSkillGapPayload): SkillGapSummary | null {
  const preMatch =
    normalizePreMatch(payload.pre_match) ??
    normalizePreMatch({
      green: payload.green,
      yellow: payload.yellow,
      red: payload.red,
    })

  const validated = payload.validated === true
  const fallbackMessage = payload.fallback_message?.trim() || undefined
  const sectionGroups =
    mapServerSectionGroups(payload.section_groups).length > 0
      ? mapServerSectionGroups(payload.section_groups)
      : preMatch
        ? groupSkillGapByResumeSection(preMatch)
        : []

  if (!validated && !fallbackMessage && !sectionGroups.length) {
    return null
  }

  if (!validated && fallbackMessage) {
    return {
      preMatch: preMatch ?? { green: [], yellow: [], red: [] },
      recommendations: [],
      greenCount: preMatch?.green.length ?? 0,
      sectionGroups: [],
      validated: false,
      fallbackMessage,
    }
  }

  if (!preMatch && !sectionGroups.length) return null

  const filtered = preMatch ? filterPreMatch(preMatch) : { green: [], yellow: [], red: [] }
  return {
    preMatch: filtered,
    recommendations: [],
    greenCount: payload.green_count ?? filtered.green.length,
    sectionGroups,
    validated: true,
  }
}

export function skillPreMatchFromBlock(block: OptimizationBlockState | undefined): SkillPreMatch | null {
  const fb = block?.agent_feedback as OptimizationAgentFeedback & { skill_pre_match?: SkillPreMatch }
  return normalizePreMatch(fb?.skill_pre_match)
}

export function skillPreMatchFromRun(run: ResumeOptimizationRun | null | undefined): SkillPreMatch | null {
  if (!run) return null
  const qe = run.quality_eval as { skill_gap?: ValidatedSkillGapPayload } | null | undefined
  if (qe?.skill_gap?.validated !== undefined || qe?.skill_gap?.section_groups) {
    const summary = summaryFromValidatedPayload(qe.skill_gap)
    return summary?.preMatch ?? null
  }
  const fromQuality = normalizePreMatch(
    (run.quality_eval as { cost_levers?: { skill_pre_match?: SkillPreMatch } } | null | undefined)
      ?.cost_levers?.skill_pre_match ?? qe?.skill_gap
  )
  if (fromQuality) return fromQuality
  const skillsBlock = run.blocks?.find((b) => b.section_type === 'skills')
  return skillPreMatchFromBlock(skillsBlock)
}

export function extractSkillGapSummary(
  run: ResumeOptimizationRun | null | undefined,
  skillsBlock?: OptimizationBlockState
): SkillGapSummary | null {
  const qe = run?.quality_eval as { skill_gap?: ValidatedSkillGapPayload } | null | undefined
  if (qe?.skill_gap && (qe.skill_gap.validated !== undefined || qe.skill_gap.section_groups)) {
    return summaryFromValidatedPayload(qe.skill_gap)
  }

  const preMatch = skillPreMatchFromBlock(skillsBlock) ?? skillPreMatchFromRun(run)
  if (!preMatch) return null
  const filtered = filterPreMatch(preMatch)
  if (!filtered.red.length && !filtered.yellow.length && !filtered.green.length) return null
  return {
    preMatch: filtered,
    recommendations: [],
    greenCount: filtered.green.length,
    sectionGroups: groupSkillGapByResumeSection(preMatch),
    validated: filtered.red.length > 0 || filtered.yellow.length > 0,
  }
}

export function skillGapHasActionItems(summary: SkillGapSummary | null): boolean {
  if (!summary) return false
  if (summary.fallbackMessage && !summary.validated) return true
  return summary.sectionGroups.some((g) => g.tone !== 'green')
}
